import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Send, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminNewsletter() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [subs, setSubs] = useState([]);
    const [subject, setSubject] = useState("");
    const [html, setHtml] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!user) return;
        if (user.role !== "admin") { nav("/"); return; }
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/newsletter/subscribers");
            setSubs(data);
        } catch { setSubs([]); }
    };

    const remove = async (id) => {
        if (!window.confirm("Rimuovere questo iscritto?")) return;
        try {
            await api.delete(`/admin/newsletter/subscribers/${id}`);
            toast.success("Rimosso");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    };

    const send = async (e) => {
        e.preventDefault();
        if (!subject.trim() || !html.trim()) {
            toast.error("Compila oggetto e contenuto");
            return;
        }
        if (!window.confirm(`Inviare la newsletter a ${subs.length} iscritti?`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/newsletter/send", { subject: subject.trim(), html });
            toast.success(`Inviate ${data.sent} su ${data.total} (${data.failed} fallite)`);
            setSubject(""); setHtml("");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore invio");
        } finally { setBusy(false); }
    };

    const loadTrailOfMonth = async () => {
        try {
            const { data } = await api.get("/admin/newsletter/template/trail-of-month");
            setSubject(data.subject || "");
            setHtml(data.html || "");
            toast.success(`Template "${data.trail?.name}" caricato. Modifica e invia.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Imposta prima un Trail del mese in /admin");
        }
    };

    if (!user) return <div className="max-w-3xl mx-auto px-5 py-20 text-zinc-400">Caricamento…</div>;

    return (
        <div data-testid="admin-newsletter-page" className="max-w-[1100px] mx-auto px-5 md:px-10 py-14 md:py-16">
            <Link to="/admin" className="text-xs uppercase tracking-[0.3em] text-zinc-400 hover:text-abnormal-red inline-flex items-center gap-2">
                <ArrowLeft size={14} /> Admin
            </Link>
            <div className="mt-6 flex items-center gap-3">
                <Mail className="text-abnormal-red" />
                <h1 className="font-display text-5xl md:text-7xl uppercase leading-none">Newsletter</h1>
            </div>
            <p className="text-zinc-400 mt-4 max-w-2xl">
                Gestisci gli iscritti e invia campagne email. Le email partono da <span className="text-abnormal-red">onboarding@resend.dev</span>.
                Su Resend in modalità free, le email arrivano solo all'indirizzo verificato del tuo account fino a quando non verifichi il dominio abnormalworld.it.
            </p>

            {/* Compose */}
            <form onSubmit={send} data-testid="newsletter-compose" className="mt-10 border border-white/10 p-6 md:p-8 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-red font-bold">Componi</p>
                    <button
                        type="button"
                        data-testid="newsletter-template-totm"
                        onClick={loadTrailOfMonth}
                        className="text-[10px] uppercase tracking-[0.25em] font-bold border border-abnormal-red/50 text-abnormal-red px-3 py-1.5 hover:bg-abnormal-red hover:text-white transition-colors"
                    >
                        ⚡ Usa template "Trail del mese"
                    </button>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-1 block">Oggetto</label>
                    <input
                        data-testid="newsletter-subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        maxLength={160}
                        className="w-full bg-black border border-zinc-800 focus:border-abnormal-red outline-none px-3 py-2 text-sm"
                    />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-1 block">Contenuto HTML (puoi usare &lt;p&gt;, &lt;a&gt;, &lt;strong&gt;, &lt;img&gt;)</label>
                    <textarea
                        data-testid="newsletter-html"
                        rows={10}
                        value={html}
                        onChange={(e) => setHtml(e.target.value)}
                        placeholder="<p>Ciao rider, sono usciti due nuovi trail...</p>"
                        className="w-full bg-black border border-zinc-800 focus:border-abnormal-red outline-none px-3 py-2 text-sm font-mono min-h-[200px]"
                    />
                </div>
                <button
                    data-testid="newsletter-send-btn"
                    type="submit"
                    disabled={busy || subs.length === 0}
                    className="bg-abnormal-red text-white uppercase tracking-wider text-sm font-bold px-6 py-3 inline-flex items-center gap-2 hover:bg-white hover:text-abnormal-red transition-colors disabled:opacity-50"
                >
                    <Send size={16} /> {busy ? "Invio in corso…" : `Invia a ${subs.length} iscritti`}
                </button>
            </form>

            {/* Subscribers */}
            <section className="mt-12">
                <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-red font-bold mb-3">Iscritti · {subs.length}</p>
                {subs.length === 0 ? (
                    <p className="text-zinc-500 text-sm uppercase tracking-wider">Nessun iscritto ancora.</p>
                ) : (
                    <div data-testid="newsletter-subscribers" className="border border-white/10 divide-y divide-white/10">
                        {subs.map((s) => (
                            <div key={s.id} className="flex items-center justify-between gap-4 px-4 py-3">
                                <div>
                                    <p className="text-sm text-white">{s.email}</p>
                                    <p className="text-[11px] text-zinc-500">{new Date(s.created_at).toLocaleString()}</p>
                                </div>
                                <button
                                    data-testid={`newsletter-delete-${s.id}`}
                                    onClick={() => remove(s.id)}
                                    className="text-zinc-500 hover:text-abnormal-red"
                                    aria-label="Rimuovi"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
